import { Link } from 'react-router-dom';
import EmptyState from '@/components/ui/EmptyState';
import type { PlayerStatusListProps, PrioritySeverity } from './types';

const statusClasses: Record<PrioritySeverity, string> = {
  neutral: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  info: 'bg-club-blue-50 text-club-blue-700 dark:bg-club-blue-950 dark:text-club-blue-300',
  warning: 'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-200',
  critical: 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-200',
  success: 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-200',
};

export default function PlayerStatusList({ ariaLabel, items, emptyTitle }: PlayerStatusListProps) {
  if (items.length === 0) return <EmptyState title={emptyTitle} className="py-10" />;

  return (
    <ul aria-label={ariaLabel} className="divide-y divide-gray-200 dark:divide-gray-800">
      {items.map((item) => (
        <li key={item.id}>
          <Link
            to={item.to}
            aria-label={[item.name, item.value, item.status].filter(Boolean).join(', ')}
            className="flex min-h-11 min-w-0 flex-wrap items-center justify-between gap-3 rounded-md px-2 py-3 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 motion-reduce:transition-none dark:hover:bg-gray-800"
          >
            <span className="min-w-0 break-words text-sm font-semibold text-gray-900 dark:text-gray-100">{item.name}</span>
            <span className="flex min-w-0 flex-wrap items-center justify-end gap-2">
              {item.value && <span className="break-words text-sm font-semibold tabular-nums text-gray-800 dark:text-gray-200">{item.value}</span>}
              {item.status && (
                <span className={`break-words rounded-full px-2 py-1 text-xs font-medium ${statusClasses[item.severity ?? 'neutral']}`}>
                  {item.status}
                </span>
              )}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
